import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Send, Building2, User, Mail, MessageSquare, Briefcase, CheckSquare, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ContactPage() {
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    reason: "",
    message: "",
    honeypot: "" // Campo oculto anti-bot
  });

  useEffect(() => {
    // Cargar motivos de contacto
    axios.get(`${API}/contact/reasons`)
      .then(res => setReasons(res.data.reasons || []))
      .catch(err => console.error("Error loading reasons:", err));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones del frontend
    if (!form.name.trim() || form.name.trim().length < 2) {
      toast.error("Por favor ingresa tu nombre completo");
      return;
    }
    if (!form.email.trim()) {
      toast.error("Por favor ingresa tu correo electrónico");
      return;
    }
    if (!form.company.trim() || form.company.trim().length < 2) {
      toast.error("Por favor ingresa el nombre de tu empresa u organización");
      return;
    }
    if (!form.reason) {
      toast.error("Por favor selecciona un motivo de contacto");
      return;
    }
    if (!form.message.trim() || form.message.trim().length < 20) {
      toast.error("Por favor describe tu solicitud con más detalle");
      return;
    }
    if (!acceptedTerms) {
      toast.error("Por favor acepta los términos para continuar");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/contact/send`, form);
      if (response.data.success) {
        setSubmitted(true);
        toast.success("¡Mensaje enviado correctamente!");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Error al enviar el mensaje";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#E91E8C] flex items-center justify-center">
              <Send size={32} className="text-white" />
            </div>
            <h1 className="text-3xl font-bold mb-4" style={{ color: '#FFE500' }}>
              ¡Mensaje Enviado!
            </h1>
            <p className="text-gray-300 mb-8">
              Gracias por contactarnos. Revisaremos tu solicitud y nos pondremos en contacto contigo pronto.
            </p>
            <Link to="/">
              <Button className="bg-[#FFE500] text-black hover:bg-[#FFE500]/90 font-bold px-8">
                <ArrowLeft size={16} className="mr-2" />
                Volver al inicio
              </Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      
      <div className="container mx-auto px-4 pt-28 pb-12 md:pt-28 md:pb-20">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#FFE500' }}>
              Contacto
            </h1>
            <p className="text-gray-400 text-sm md:text-base max-w-lg mx-auto">
              Este formulario es exclusivamente para asuntos profesionales como contrataciones, 
              licenciamiento, patrocinios y prensa.
            </p>
            <p className="text-gray-500 text-xs mt-3">
              ¿Eres fan y quieres saludar? ¡Síguenos en redes sociales! 💛
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Honeypot - campo oculto anti-bot */}
            <input
              type="text"
              name="website"
              value={form.honeypot}
              onChange={(e) => setForm({ ...form, honeypot: e.target.value })}
              style={{ position: 'absolute', left: '-9999px' }}
              tabIndex={-1}
              autoComplete="off"
            />

            {/* Motivo de contacto */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Briefcase size={14} className="inline mr-2 text-[#E91E8C]" />
                Motivo de contacto *
              </label>
              <Select
                value={form.reason}
                onValueChange={(v) => setForm({ ...form, reason: v })}
              >
                <SelectTrigger className="bg-gray-900 border-gray-700 text-white h-12" data-testid="contact-reason">
                  <SelectValue placeholder="Selecciona el motivo..." />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700">
                  {reasons.map((reason) => (
                    <SelectItem key={reason} value={reason} className="text-white hover:bg-gray-800">
                      {reason}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Nombre y Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <User size={14} className="inline mr-2 text-[#E91E8C]" />
                  Nombre completo *
                </label>
                <Input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Tu nombre"
                  className="bg-gray-900 border-gray-700 text-white h-12"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <Mail size={14} className="inline mr-2 text-[#E91E8C]" />
                  Correo electrónico *
                </label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="tu@empresa.com"
                  className="bg-gray-900 border-gray-700 text-white h-12"
                  data-testid="contact-email"
                />
              </div>
            </div>

            {/* Empresa */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Building2 size={14} className="inline mr-2 text-[#E91E8C]" />
                Empresa / Organización *
              </label>
              <Input
                type="text"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                placeholder="Nombre de la empresa"
                className="bg-gray-900 border-gray-700 text-white h-12"
                data-testid="contact-company"
              />
            </div>

            {/* Mensaje */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <MessageSquare size={14} className="inline mr-2 text-[#E91E8C]" />
                Describe tu solicitud *
              </label>
              <Textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Cuéntanos sobre tu proyecto, evento o propuesta..."
                rows={5}
                className="bg-gray-900 border-gray-700 text-white resize-none"
                data-testid="contact-message"
              />
              <p className="text-xs text-gray-500 mt-1">Mínimo 20 caracteres</p>
            </div>

            {/* Checkbox de aceptación */}
            <div 
              className="flex items-start gap-3 cursor-pointer group"
              onClick={() => setAcceptedTerms(!acceptedTerms)}
              data-testid="accept-terms-checkbox"
            >
              <div className="mt-0.5 flex-shrink-0">
                {acceptedTerms ? (
                  <CheckSquare size={20} className="text-[#E91E8C]" />
                ) : (
                  <Square size={20} className="text-gray-500 group-hover:text-gray-400" />
                )}
              </div>
              <p className="text-xs text-gray-400 leading-relaxed">
                Acepto que este correo es de carácter profesional y que mi acceso al sitio puede ser restringido si no me adhiero a los{" "}
                <Link to="/terminos" onClick={(e) => e.stopPropagation()} className="text-[#E91E8C] hover:underline">Términos de Servicio</Link>
                {" "}y{" "}
                <Link to="/privacidad" onClick={(e) => e.stopPropagation()} className="text-[#E91E8C] hover:underline">Política de Privacidad</Link>.
              </p>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading || !acceptedTerms}
              className="w-full h-14 text-lg font-bold bg-[#E91E8C] hover:bg-[#E91E8C]/90 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="contact-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⏳</span>
                  Enviando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send size={18} />
                  Enviar Mensaje
                </span>
              )}
            </Button>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
}
